import React, { useEffect, useState } from "react";
import NavBar from "../element/NavBar";
import Footer from "../element/Footer";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BaseApi from "../api/BaseApi";
import Cookies from "js-cookie";
import { useTranslation } from "react-i18next";

const JobseekerList = () => {
  const navigate = useNavigate();
  const [t] = useTranslation("global");

  const primaryColor = Cookies.get("primaryColor");
  const secondaryColor = Cookies.get("secondaryColor");
  const mapKey = Cookies.get("mapKey");

const [catId, setCatId] = useState(
  () => sessionStorage.getItem("catId") || ""
);

  const [loading, setLoading] = useState(false);
  const [searchButton, setSearchButton] = useState(false);

  const [filterItem, setFilterItem] = useState({
    keyword: "",
    category_id: "",
    location: "",
  });

  const [jobseekers, setJobseekers] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [categories, setCategories] = useState([]); // categories

  // ---------------- LOCATION STATES ----------------
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionTaken, setSuggestionTaken] = useState(false);

  // ---------------- FETCH DATA ----------------
const getData = async () => {
  try {
    setLoading(true);

    const categoryIdToUse = catId || filterItem.category_id;

    let res;

    if (categoryIdToUse) {
      res = await axios.post(`${BaseApi}/candidates/index`, {
        ...filterItem,
        category_id: categoryIdToUse,
      });
    } else {
      res = await axios.post(`${BaseApi}/candidates/index`);
    }

    setJobseekers(res?.data?.response?.candidates || []);
    setCategories(res?.data?.response?.categories || []);
  } catch (err) {
    console.error("Failed to fetch jobseekers");
  } finally {
    setLoading(false);
  }
};


useEffect(() => {
  getData();
  window.scrollTo(0, 0);
}, [catId]);

useEffect(() => {
  if (catId) {
    setFilterItem((prev) => ({
      ...prev,
      category_id: String(catId),
    }));
  }
}, [catId]);



  // ---------------- LOAD GOOGLE MAP ----------------
  useEffect(() => {
    if (!mapKey) return;

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${mapKey}&libraries=places`;
    script.async = true;

    script.onload = () => {
      if (window.google) {
        setAutocompleteService(
          new window.google.maps.places.AutocompleteService(),
        );
      }
    };

    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [mapKey]);

  // ---------------- INPUT HANDLER ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilterItem((prev) => ({ ...prev, [name]: value }));
  };

  // ---------------- LOCATION CHANGE ----------------
  const handleLocationChange = (e) => {
    const value = e.target.value;
    setSuggestionTaken(false);

    setFilterItem((prev) => ({
      ...prev,
      location: value,
    }));

    if (!autocompleteService || value.length < 2) {
      setSuggestions([]);
      return;
    }

    autocompleteService.getPlacePredictions(
      {
        input: value,
        types: ["(cities)"],
      },
      (predictions, status) => {
        if (status === "OK" && predictions) {
          setSuggestions(predictions.map((p) => p.description));
        } else {
          setSuggestions([]);
        }
      },
    );
  };

  const handleSuggestionClick = (value) => {
    setFilterItem((prev) => ({
      ...prev,
      location: value,
    }));

    setSuggestionTaken(true);
    setSuggestions([]);
  };

  // ---------------- SEARCH ----------------
  const handleSearch = async (e) => {
    e.preventDefault();

    if (!filterItem.keyword && !filterItem.category_id && !filterItem.location)
      return;

    try {
      setLoading(true);
      setSearchButton(true);

      const res = await axios.post(`${BaseApi}/candidates/index`, filterItem);

      setSearchData(res?.data?.response?.candidates || []);
    } catch {
      console.log("Search failed");
    } finally {
      setLoading(false);
    }
  };

  // ---------------- RESET ----------------
const handleReset = (e) => {
  e.preventDefault();

  sessionStorage.removeItem("catId");
  setCatId(""); // 👈 important

  setSearchButton(false);
  setSearchData([]);
  setFilterItem({
    keyword: "",
    category_id: "",
    location: "",
  });

  setSuggestions([]);
};



  // ---------------- PAGINATION ----------------
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  const data = searchButton ? searchData : jobseekers;
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentData = data.slice(indexOfFirst, indexOfLast);

  return (
    <>
      <NavBar />

      {loading ? (
        <div className="loader-container"></div>
      ) : (
        <>
          {/* ---------------- SEARCH BAR ---------------- */}
          <div className="SJPSection1 jobseekerList">
            <form>
              <div className="formItems">
                <input
                  className="form-control me-2"
                  name="keyword"
                  value={filterItem.keyword}
                  placeholder={t("searchJobPage.keyword")}
                  onChange={handleChange}
                />

                <select
                  className="form-select me-2"
                  name="category_id"
                  value={filterItem.category_id}
                  placeholder={t("searchJobPage.anyCategory")}
                  onChange={handleChange}
                  style={{
                    minWidth: "none",
                  }}
                >
                  <option value="">{t("searchJobPage.anyCategory")}</option>
                  {categories.map((item, index) => (
                    <option key={index} value={item.id}>
                      {item.name}
                    </option>
                  ))}
                </select>

                {/* LOCATION WITH GOOGLE SUGGESTION */}
                <div className="position-relative me-2 w-100">
                  <input
                    className="form-control"
                    name="location"
                    value={filterItem.location}
                    placeholder={t("searchJobPage.enterLocation")}
                    onChange={handleLocationChange}
                    autoComplete="off"
                  />

                  {suggestions.length > 0 && !suggestionTaken && (
                    <div className="suggestionsSmall">
                      <ul className="locationDropdown">
                        {suggestions.map((item, index) => (
                          <li
                            key={index}
                            onClick={() => handleSuggestionClick(item)}
                          >
                            <div className="eachLocation">
                              <div className="locationIcon">
                                <LocationOnIcon fontSize="small" />
                              </div>
                              <div className="locationSuggestion">{item}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <button
                  className="searchItems me-2 w-100 btn searchJobseekerButtons"
                  style={{ background: primaryColor, color: "#fff" }}
                  onClick={handleSearch}
                >
                  {t("searchJobPage.findJobseekers")}
                </button>

                <button
                  className="searchItems me-2 btn searchButtons"
                  style={{ background: secondaryColor, color: "#fff" }}
                  onClick={handleReset}
                >
                  {t("searchJobPage.clear")}
                </button>
              </div>
            </form>
          </div>

          {/* ---------------- TABLE ---------------- */}
          <div className="container my-4">
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-light">
                  <tr>
                    <th>Worker Name</th>
                    <th>Contact Number</th>
                    <th>Category</th>
                    {/* <th>Sub-Category</th> */}
                    <th>Location</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((item, index) => (
                      <tr key={index}>
                        <td>
                          {item.first_name} {item.last_name}
                        </td>
                        <td>{item.contact || "—"}</td>
                        <td>
                          {Array.isArray(item.category_names)
                            ? item.category_names.join(", ")
                            : item.category_names || "—"}
                        </td>
                        {/* <td>
                          {Array.isArray(item.interest_sub_categories)
                            ? item.interest_sub_categories.join(", ")
                            : item.interest_sub_categories || "—"}
                        </td> */}
                        <td>{item.location || item.city || "—"}</td>
                        <td>
                          <button
                            className="btn btn-sm"
                            style={{
                              background: primaryColor,
                              color: "#fff",
                            }}
                            onClick={() =>
                              navigate(`/jobseeker/profile/${item.slug}`)
                            }
                          >
                            view
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="20" className="text-center py-4">
                        No jobseekers found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* ---------------- PAGINATION ---------------- */}
            <div className="d-flex justify-content-between align-items-center mt-3">
              <p className="text-muted">
                Showing {indexOfFirst + 1}–{Math.min(indexOfLast, data.length)}{" "}
                of {data.length}
              </p>

              <div>
                <button
                  className="btn me-2"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Prev
                </button>

                <button
                  className="btn"
                  disabled={indexOfLast >= data.length}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          <Footer />
        </>
      )}
    </>
  );
};

export default JobseekerList;
